// Inline Markdown → HTML. Deliberately inline-only: locale strings are single
// sentences and the output has to stay renderable inside <Trans>.

const ESCAPED_CHAR = /\\([\\`*_~[\]()])/g;
const CODE_SPAN = /`([^`\n]+)`/g;
const LINK = /\[([^\]\n]+)\]\(\s*([^\s()]+)\s*\)/g;
const BOLD_STAR = /\*\*(?=\S)([\s\S]*?\S)\*\*/g;
const BOLD_UNDERSCORE = /(^|[^\w\\])__(?=\S)([\s\S]*?\S)__(?!\w)/g;
const ITALIC_STAR = /(^|[^*\w\\])\*(?=\S)([^*\n]*?\S)\*(?!\*)/g;
const ITALIC_UNDERSCORE = /(^|[^_\w\\])_(?=\S)([^_\n]*?\S)_(?!\w)/g;
const STRIKETHROUGH = /~~(?=\S)([\s\S]*?\S)~~/g;

const MARKDOWN_HINT = /[*_~`[\\]/;
const SAFE_URL = /^(?:https?:\/\/|mailto:|tel:|\/|#)/i;

// Converted fragments are parked behind NUL markers so later passes can't
// re-parse a URL or the inside of a code span as emphasis.
const MARK = "\u0000";
const MARKED = /\u0000(\d+)\u0000/g;

function escapeHtml(text) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Converts basic inline Markdown to HTML.
 *
 * Supported: `**bold**`, `__bold__`, `*italic*`, `_italic_`, `~~strikethrough~~`,
 * `` `code` ``, `[label](url)` and backslash escapes (`\*`). Underscores and
 * asterisks inside words (`first_name`, `campaign_*`) are left alone, and
 * anything already in HTML — including the `<0>` placeholders `<Trans>` uses —
 * passes through untouched.
 *
 * @param {string} text - Markdown source.
 * @returns {string} HTML string, or the input unchanged when it holds no Markdown.
 */
export default function markdownToHtml(text) {
  if (typeof text !== "string" || !MARKDOWN_HINT.test(text)) return text;

  const marked = [];
  const mark = (html) => {
    marked.push(html);
    return `${MARK}${marked.length - 1}${MARK}`;
  };

  const html = text
    .replace(ESCAPED_CHAR, (_match, char) => mark(escapeHtml(char)))
    .replace(CODE_SPAN, (_match, code) =>
      mark(`<code>${escapeHtml(code)}</code>`),
    )
    .replace(LINK, (match, label, url) =>
      SAFE_URL.test(url)
        ? mark(
            `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">`,
          ) +
          label +
          mark("</a>")
        : match,
    )
    .replace(BOLD_STAR, "<strong>$1</strong>")
    .replace(BOLD_UNDERSCORE, "$1<strong>$2</strong>")
    .replace(ITALIC_STAR, "$1<em>$2</em>")
    .replace(ITALIC_UNDERSCORE, "$1<em>$2</em>")
    .replace(STRIKETHROUGH, "<s>$1</s>");

  return html.replace(MARKED, (_match, index) => marked[Number(index)]);
}

/**
 * Runs {@link markdownToHtml} over every string of an i18next resource tree.
 *
 * @param {object|Array|string} resources - Locale bundle, or any nested part of one.
 * @returns {object|Array|string} Same shape, with Markdown converted to HTML.
 */
export function markdownToHtmlDeep(resources) {
  if (typeof resources === "string") return markdownToHtml(resources);
  if (Array.isArray(resources)) return resources.map(markdownToHtmlDeep);
  if (resources && typeof resources === "object") {
    return Object.fromEntries(
      Object.entries(resources).map(([key, value]) => [
        key,
        markdownToHtmlDeep(value),
      ]),
    );
  }
  return resources;
}
